<template>
  <!-- 관리자 예약 상세 '컨셉 변경'에서 할인 체험단 예약을 정가 컨셉으로 바꿀 때 여는 창: 받을 차액을 보이고 손님 결제 QR을 띄운다. -->
  <v-dialog :value="true" width="460" max-width="calc(100vw - 32px)" content-class="rounded-xl" persistent @keydown.esc="close">
    <v-card>
      <v-card-title>컨셉 변경 차액</v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle class="black--text text-body-1 font-weight-medium mt-2" style="word-break: keep-all">
        {{ $getUserDisplayName(reservation.name) }} 예약 · {{ q.fromConcept }} → {{ q.toConcept }}
      </v-card-subtitle>

      <v-card-text v-if="phase === 'quote'" class="px-6">
        <dl class="diff-table">
          <div>
            <dt>정가<template v-if="q.persons > 1">({{ q.persons }}명)</template></dt>
            <dd>{{ won(q.regularMain) }}</dd>
          </div>
          <div>
            <dt>낸 금액(할인 체험단)</dt>
            <dd>− {{ won(q.chargedMain) }}</dd>
          </div>
          <div v-if="q.paid">
            <dt>이미 받은 차액</dt>
            <dd>− {{ won(q.paid) }}</dd>
          </div>
          <div class="diff-total">
            <dt>받을 차액</dt>
            <dd>{{ won(q.amount) }}</dd>
          </div>
        </dl>
        <p v-if="!noDifference" class="text-body-2 grey--text text--darken-1 mb-0 mt-4" style="word-break: keep-all">
          QR을 띄워 손님 휴대폰으로 결제받습니다. 결제가 끝나면 예약 컨셉이 바로 바뀝니다.
        </p>
      </v-card-text>

      <v-card-text v-else-if="phase === 'qr'" ref="phaseBody" tabindex="-1" class="phase-body px-6 text-center">
        <AddonPayQr :url="payUrl" :amount-text="won(order.amount)" :waiting="QR_TEXT.waiting">
          <span>손님 휴대폰 카메라로 찍게 해 주세요. 결제가 끝나면 이 창이 저절로 바뀝니다.</span>
          <span>창을 닫아도 손님이 결제를 마치면 예약 컨셉은 바뀝니다.</span>
        </AddonPayQr>
      </v-card-text>

      <v-card-text v-else-if="phase === 'paid'" ref="phaseBody" tabindex="-1" class="phase-body px-6 text-body-1 font-weight-bold black--text" style="word-break: keep-all">{{ paidText }}</v-card-text>

      <!-- 화면 읽기 프로그램이 단계가 바뀐 것을 읽도록, 알림 자리는 늘 두고 글만 바꾼다 -->
      <p class="d-sr-only" role="status">{{ statusText }}</p>
      <div class="px-6" :class="{ 'text-center': phase === 'qr' }" role="alert">
        <p v-if="alertText" class="pt-3 mb-0 text-body-2 font-weight-bold" style="color: #a53a66; word-break: keep-all">{{ alertText }}</p>
      </div>

      <v-card-actions class="pb-4">
        <v-spacer />
        <v-btn ref="closeBtn" class="px-4" depressed outlined :disabled="busy" :loading="busy && phase === 'qr'" @click="close">닫기</v-btn>
        <v-btn v-if="phase === 'quote' && !noDifference" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" @click="createOrder">QR 띄우기</v-btn>
        <v-btn v-if="phase === 'paidNotApplied'" ref="applyBtn" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" @click="applyConcept">컨셉 바꾸기</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AddonPayQr from "./AddonPayQr"
import addonQrPay, { REQUEST_TIMEOUT_MS } from "./addonQrPay"

// 이 화면에서 이미 띄운 차액 주문(예약·컨셉·금액이 같으면 새로 만들지 않고 같은 QR을 다시 띄운다).
// 창을 닫았다 다시 열 때 결제할 수 있는 주문이 둘 생겨, 손님이 앞 QR로 결제했는데 직원 창은 뒤 QR을 기다리는 일을 막는다.
const OPEN_ORDERS = new Map()
const TEXT = {
  notApplied: "결제는 받았지만 예약 컨셉이 바뀌지 않았습니다. '컨셉 바꾸기'를 눌러 주세요.",
  orderFailed: "결제 QR을 만들지 못했습니다. 잠시 뒤 다시 눌러 주세요.",
  noDifference: "받을 차액이 없어졌습니다. 창을 닫고 컨셉을 다시 골라 주세요.",
  applyFailed: "컨셉을 바꾸지 못했습니다. 잠시 뒤 다시 눌러 주세요.",
}

export default {
  name: "ConceptChangeDiffDialog",

  components: { AddonPayQr },

  mixins: [addonQrPay],

  props: {
    // 관리자 예약 목록의 한 줄(id가 예약 번호)
    reservation: { type: Object, required: true },
    // POST /api/addon-pay/concept-change/quote 응답
    quote: { type: Object, required: true },
  },

  data: () => ({
    phase: "quote", // quote | qr | paid | paidNotApplied
    // 주문을 만들려는데 받을 차액이 없어졌으면(그새 받음 등) 서버가 다시 계산한 값과 그 표시
    serverQuote: null,
    noDifference: false,
  }),

  computed: {
    q() {
      return this.serverQuote || this.quote
    },
    paidText() {
      return `${this.won(this.order && this.order.amount)} 결제를 받았고 예약 컨셉을 바꿨습니다.`
    },
    alertText() {
      if (this.errorMessage) return this.errorMessage
      if (this.phase === "paidNotApplied") return TEXT.notApplied
      if (this.qrOffline) return this.QR_TEXT.offline
      return ""
    },
  },

  methods: {
    payParam() {
      return "change"
    },

    statusUrl() {
      return this.statusUrlOf(this.order)
    },

    statusUrlOf(order) {
      return `/api/addon-pay/concept-change/${order.orderId}`
    },

    // 결제됐는데 안 바뀜 → '컨셉 바꾸기', 받을 차액이 없어져 'QR 띄우기'가 사라지면 '닫기', QR 기다림·결제 받음 → 그 단계 본문
    focusTarget() {
      if (this.phase === "paidNotApplied") return this.$refs.applyBtn
      if (this.noDifference && this.phase === "quote") return this.$refs.closeBtn
      return this.$refs.phaseBody
    },

    orderKey() {
      return `${this.q.reservationId}:${this.q.toConceptId}:${this.q.amount}`
    },

    async createOrder() {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ""
      try {
        let order = OPEN_ORDERS.get(this.orderKey())
        if (order && !(await this.stillOpen(order))) {
          OPEN_ORDERS.delete(this.orderKey())
          order = null
        }
        if (!order) {
          const { data } = await this.$http.post(
            "/api/addon-pay/concept-change/order",
            { reservationId: this.q.reservationId, conceptId: this.q.toConceptId },
            { timeout: REQUEST_TIMEOUT_MS }
          )
          order = data
          OPEN_ORDERS.set(this.orderKey(), order)
        }
        this.order = order
        this.phase = "qr"
        this.poll()
      } catch (error) {
        const body = (error && error.response && error.response.data) || {}
        if (body.code === "NO_DIFFERENCE") {
          this.serverQuote = body.quote || null
          this.noDifference = true
          this.focusPhase()
        }
        this.errorMessage = body.code === "NO_DIFFERENCE" ? TEXT.noDifference : TEXT.orderFailed
      } finally {
        this.busy = false
      }
    },

    finish(data) {
      clearTimeout(this.timer)
      OPEN_ORDERS.delete(this.orderKey())
      if (!data.applied) {
        this.phase = "paidNotApplied"
        return
      }
      this.phase = "paid"
      this.$emit("changed", this.q.toConcept)
    },

    // 결제는 받았는데 예약 컨셉이 안 바뀌었을 때: 예전 컨셉 변경 경로로 바꾼다.
    async applyConcept() {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ""
      try {
        const { data } = await this.$http.post(
          "/api/reservation/update/change_concept",
          { concept: this.q.toConcept, id: this.reservation.id },
          { timeout: REQUEST_TIMEOUT_MS }
        )
        if (!data.affectedRows) throw new Error("not changed")
        this.finish({ applied: true })
      } catch (error) {
        this.errorMessage = TEXT.applyFailed
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped src="./addonPayDialog.css"></style>
